import { useState, useEffect, useRef, ChangeEvent, FormEvent } from 'react';
import { useLazyQuery } from '@apollo/client';
import triggerTracking from '@blueprint/tracking';
import { SearchSmall as SearchSmallZDS } from '@zoopla-eng/zds-components/icons';
import { getTrackedPropertyTaxonomyData } from '@zoopla/analytics/property-data';
import { AddressByPostcodeQuery } from '../queries/addressByPostcode.query';
import { ButtonLeft } from '@zoopla-eng/zds-components/button';
import {
    AddressesByPostcodeQuery,
    AddressesByPostcodeQueryVariables,
} from '../queries/addressByPostcode.query.generated';
import { POSTCODE_REGEX } from '@zoopla/shared/forms';
import { styled } from '@zoopla-eng/zds-components/themes';
import { InputField } from '@zoopla-eng/zds-components/input-field';
import { Select } from '@zoopla-eng/zds-components/select';

const ContentWrapper = styled('div', {
    marginBottom: '$S500',
});

const SubmitButton = styled(ButtonLeft, {
    width: '100%',
    '@M100': {
        width: 'auto',
    },
});

const redirect = (path: string) => {
    window.location.assign(path);
};

const addressPlaceholderOption = {
    id: '',
    text: 'Please select an address',
};

type Option = {
    id: string;
    text: string;
};

type PostcodeInputErrors =
    | 'none'
    | 'postcodeValidation'
    | 'noProperties'
    | 'fetchError';

export const errorMessages: Record<PostcodeInputErrors, string> = {
    postcodeValidation: 'Please provide a full, valid postcode e.g. SW1 0RG',
    noProperties:
        'There was an error fetching properties for that postcode. Please try another one.',
    fetchError:
        'There was an error fetching properties for that postcode. Please try again.',
    none: '',
};

const formatPostcode = (postcode: string) => {
    const trimmedPostcode = postcode.trim().replace(/ /g, '');

    if (trimmedPostcode.length <= 3 || trimmedPostcode.indexOf(' ') > -1) {
        return trimmedPostcode;
    }

    const postcodeLength = trimmedPostcode.length;
    // the outcode is always 3 characters long
    const outcode = trimmedPostcode.slice(postcodeLength - 3, postcodeLength);
    const postcodeStart = trimmedPostcode.slice(0, postcodeLength - 3);

    return `${postcodeStart} ${outcode}`;
};

function useAddressSelectFocus(addresses: Option[]) {
    const addressSelectRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (addresses.length > 0) addressSelectRef.current?.focus();
    }, [addresses]);

    return addressSelectRef;
}

export const HomeValuationForm = ({
    eventCategory,
}: {
    eventCategory: string;
}) => {
    const [postcodeValue, setPostcodeValue] = useState('');
    const [postcodeInputError, setPostcodeInputError] =
        useState<PostcodeInputErrors>('none');
    const [addressSelectErrorState, setAddressSelectErrorState] =
        useState<string>('');

    const [selectedAddressOption, setSelectedAddressOption] = useState<Option>(
        addressPlaceholderOption
    );
    const [addresses, setAddresses] = useState<Option[]>([
        addressPlaceholderOption,
    ]);
    const addressSelectRef = useAddressSelectFocus(addresses);

    const onAddressFetchSuccess = async (data: AddressesByPostcodeQuery) => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'click',
            eventLabel: 'locate_address',
        });

        const addressesFromApi = data?.addressesByPostcode?.addresses.map(
            (address) => ({
                id: address?.uprn as string,
                text: address?.fullAddress as string,
            })
        );

        if (Array.isArray(addressesFromApi) && addressesFromApi.length) {
            const allAddresses = [addressPlaceholderOption].concat(
                addressesFromApi
            );
            setAddresses(allAddresses);
        } else {
            setAddresses([]);
            setPostcodeInputError('noProperties');
        }
    };

    const [fetchAddresses, { loading: fetchingAddresses }] = useLazyQuery<
        AddressesByPostcodeQuery,
        AddressesByPostcodeQueryVariables
    >(AddressByPostcodeQuery, {
        onError: function (err) {
            setAddresses([]);
            if (
                err?.graphQLErrors?.[0]?.extensions?.['reason'] ===
                'POSTCODE_NOT_FOUND'
            ) {
                setPostcodeInputError('noProperties');
            } else {
                setPostcodeInputError('fetchError');
            }
            return;
        },
        onCompleted: onAddressFetchSuccess,
        fetchPolicy: 'cache-and-network',
    });

    const onPostcodeChange = (evt: ChangeEvent<HTMLInputElement>) => {
        setPostcodeValue(evt.target.value);
        setAddresses([]);

        if (postcodeInputError !== 'none') {
            setPostcodeInputError('none');
        }
    };

    const redirectToPropertyDetailsPage = () => {
        const selectedUPRN = selectedAddressOption.id;

        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'clickthrough',
            eventLabel: 'get_estimate',
            taxonomy: getTrackedPropertyTaxonomyData({ uprn: selectedUPRN }),
        });

        const propertyDetailsPageRoute = `/property/uprn/${selectedUPRN}/`;
        redirect(propertyDetailsPageRoute);
    };

    const hasFetchedAddresses = addresses && addresses.length > 1;

    const buttonText = hasFetchedAddresses
        ? 'Get instant valuation'
        : 'Look up postcode';

    const onSubmit = (evt: FormEvent) => {
        evt.preventDefault();
        if (hasFetchedAddresses) {
            if (selectedAddressOption.id === addressPlaceholderOption.id) {
                return setAddressSelectErrorState(
                    'Please select an address to get an instant valuation'
                );
            }
            redirectToPropertyDetailsPage();
            return;
        }

        const formattedPostcode = formatPostcode(postcodeValue);
        const isValid = POSTCODE_REGEX.test(formattedPostcode);

        if (!isValid) {
            return setPostcodeInputError('postcodeValidation');
        }

        setPostcodeInputError('none');
        fetchAddresses({
            variables: { postcode: formattedPostcode },
        });
    };

    return (
        <form onSubmit={onSubmit} data-testid="home-estimate-form">
            <ContentWrapper>
                <InputField
                    name="postcode"
                    placeholder="e.g. SW1 0RG"
                    label="Enter full UK postcode"
                    data-testid="home-valuation-postcode-input"
                    errorText={errorMessages[postcodeInputError]}
                    onChange={onPostcodeChange}
                    value={postcodeValue}
                />
            </ContentWrapper>

            {hasFetchedAddresses ? (
                <ContentWrapper data-testid="home-valuation-address-select-container">
                    <Select
                        ref={addressSelectRef}
                        disabled={addresses.length <= 1}
                        options={addresses}
                        label="Address"
                        onSelectedOptionChange={(option) => {
                            setAddressSelectErrorState('');
                            setSelectedAddressOption(option);
                        }}
                        selectedOption={selectedAddressOption}
                        errorText={addressSelectErrorState}
                    />
                </ContentWrapper>
            ) : null}
            <SubmitButton
                category="secondary"
                data-testid="home-valuation-button"
                icon={<SearchSmallZDS />}
                loading={fetchingAddresses}
                loadingText={buttonText}
            >
                {buttonText}
            </SubmitButton>
        </form>
    );
};
